import type { Blueprint } from '@builder/base/blueprints/Blueprint';
import type { ValidatableBlueprint } from '@builder/base/blueprints/ValidatableBlueprint';
import type { HasWidth } from '@builder/base/traits/HasWidth';
import type { ValidationErrors } from '@builder/base/types/ValidationErrors';
import type { FormBuilderContract } from '@builder/form';
import type { BlueprintDefinition } from '@builder/form/blueprints/BlueprintDefinition';
import type { CustomErrorBlueprint } from '@builder/form/blueprints/CustomErrorBlueprint';
import type { ReadonlyBlueprint } from '@builder/form/blueprints/ReadonlyBlueprint';
import type { RequiredBlueprint } from '@builder/form/blueprints/RequiredBlueprint';
import type { VisibleBlueprint } from '@builder/form/blueprints/VisibleBlueprint';
import type { Entry } from '@builder/form/entries/Entry';
import { entry, instanceOfEntry } from '@builder/form/entries/Entry';
import { ValidEntry } from '@builder/form/entries/ValidEntry';
import { AlwaysChoice } from '@builder/form/enums/AlwaysChoice';
import type { InternallyChoice } from '@builder/form/enums/InternallyChoice';
import { NeverChoice } from '@builder/form/enums/NeverChoice';
import type { WhenChoice } from '@builder/form/enums/WhenChoice';
import type { EntryFactory } from '@builder/form/traits/EntryFactory';
import type { HasFilter } from '@builder/form/traits/HasFilter';
import type { HasHelp } from '@builder/form/traits/HasHelp';
import type { HasLabel } from '@builder/form/traits/HasLabel';
import type { HasPresenter } from '@builder/form/traits/HasPresenter';
import type { ProcessCallback } from '@builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'attachments',
    name: '[[[Załączniki]]]',
    icon: 'fa-upload',
    group: 'primary',
    position: 6,
    disabled: false
};

export class AttachmentsEntry extends ValidEntry<any[]>
{
    public type: string = Definition.type;
    public data: any[] = null;
    public hasValues?: boolean = null;

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }
    }

    public async collect(blueprint: AttachmentsContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        const result = await preprocess(blueprint, this, form.blueprintId, form.entryId);

        return entry({
            type: this.type,
            data: this.data?.map(p => except(p, 'upload', 'thumb')),
            ...(result ?? {})
        });
    }

    public validate(blueprint: AttachmentsContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        if (!form.expressions.readonly(blueprint, true) && form.expressions.visible(blueprint, true))
        {
            if (form.expressions.required(blueprint) && (this.data == null || this.data.length == 0))
            {
                this.errors.values = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if (form.expressions.customError(blueprint))
            {
                this.errors.custom = [form.expressions.customErrorMessage(blueprint)];
            }
        }

        return this.valid();
    }

    public async upload(blueprint: AttachmentsContract, storageService: (file: File, index: number) => Promise<any>): Promise<void>
    {
        const attachments = this.data || [];

        for (let i = 0; i < attachments.length; i++)
        {
            const attachment = attachments[i];

            if (attachment && attachment.upload)
            {
                attachments[i] = await storageService(attachment.upload, i);
            }
        }

        this.data = attachments;
    }
}

export const instanceOfAttachmentsEntry = (object: any): object is AttachmentsEntry =>
{
    return instanceOfEntry(object) && 'type' in object && object.type === Definition.type;
};

export interface AttachmentsContract extends Blueprint, VisibleBlueprint, ReadonlyBlueprint, RequiredBlueprint, CustomErrorBlueprint, HasLabel, HasHelp, HasWidth, HasFilter, HasPresenter
{
    extensions: string;
    maxFileSize: number;
    maxFiles: number;
    optimizeImage: boolean;
    convertTo: null | 'webp';
}

export class AttachmentsType implements AttachmentsContract, ValidatableBlueprint, EntryFactory<AttachmentsEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public extensions: string;
    public maxFileSize: number;
    public maxFiles: number;
    public help: Record<string, string>;
    public minWidth: number;
    public width: number;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public customError: NeverChoice | WhenChoice;
    public customErrorWhen: string;
    public customErrorMessage: Record<string, string>;
    public errors: ValidationErrors;
    public showFilter: boolean;
    public optimizeImage: boolean;
    public convertTo: null | 'webp';

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Załączniki' };
        this.showLabel = true;
        this.extensions = '';
        this.maxFileSize = 10;
        this.maxFiles = 1;
        this.help = {};
        this.minWidth = 2;
        this.width = 0;
        this.customError = NeverChoice.Never;
        this.customErrorWhen = null;
        this.customErrorMessage = {};
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.showFilter = false;
        this.optimizeImage = false;
        this.convertTo = null;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): AttachmentsEntry
    {
        return new AttachmentsEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors
        };
    }

    public getColumnExpression(): string | null
    {
        return `${this.name}$..id`;
    }
}
